import React, { createContext, useContext, useState, useEffect } from 'react';
import en from '../locales/en';
import es from '../locales/es';
import fr from '../locales/fr';

const translations = { en, es, fr };

const LanguageContext = createContext();

export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' }
];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Only English is enabled currently — force 'en' regardless of saved/browser preference.
    // This ensures returning visitors who previously chose ES/FR see the default English UI.
    const saved = localStorage.getItem('language');
    if (saved && translations[saved] && languages.find(l => l.code === saved)) {
      return saved;
    }
    return 'en';
  });

  const [dir, setDir] = useState('ltr');

  useEffect(() => {
    // Save language preference
    localStorage.setItem('language', language);
    
    // Update document direction for RTL languages
    const langConfig = languages.find(l => l.code === language);
    const newDir = langConfig?.dir || 'ltr';
    setDir(newDir);
    document.documentElement.dir = newDir;
    document.documentElement.lang = language;
  }, [language]);

  // Translation function with nested key support
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English
        value = translations['en'];
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return key if translation not found
          }
        }
        break;
      }
    }
    
    return typeof value === 'string' ? value : key;
  };

  const changeLanguage = (langCode) => {
    if (translations[langCode]) {
      setLanguage(langCode);
    }
  };

  const currentLanguage = languages.find(l => l.code === language) || languages[0];

  return (
    <LanguageContext.Provider value={{ 
      language, 
      changeLanguage, 
      t, 
      dir,
      currentLanguage,
      languages,
      isRTL: dir === 'rtl'
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
